import { Route, Navigate } from "react-router-dom";
import { Suspense } from "react";
import { CircularProgress, Stack } from "@mui/material";
import Dashboard from "../components/Dashboard/Dashboard";
import LoginPage from "../components/LoginPage/LoginPage";
import RegisterPage from "../components/RegisterPage/RegisterPage";
import ForgotPasswordPage from "../components/ForgotPasswordPage/ForgotPasswordPage";
import ResetPasswordPage from "../components/ResetPasswordPage/ResetPasswordPage";
import VerifyEmailPage from "../components/VerifyEmailPage/VerifyEmailPage";
import AuthPage from "../pages/auth/AuthPage";
import AccountSettings from "../pages/auth/AccountSettings";
import { AuthProtectedRoute } from "./AuthProtectedRoute";
import { AdminProtectedRoute } from "./AdminProtectedRoute";
import { GuestOnlyRoute } from "./GuestOnlyRoute";
import { ActiveMembershipProtectedRoute } from "./ActiveMembershipProtectedRoute";
import { MembershipSuccess } from "../components/MembershipSuccess/MembershipSuccess";
import ChartsPage from "../components/Dashboard/Charts/ChartsPage";
import ComparisonPage from "../pages/Compare/ComparisonPage";
import PortfolioPage from "../components/Portfolio/PortfolioPage";
import PortfolioManagePage from "../components/Portfolio/PortfolioManagePage";
import { SidebarProvider } from "../contexts/SidebarContext";
import Trading212ConfigurationPage from "../components/Portfolio/Trading212/Trading212ConfigurationPage";
import Trading212CsvUploadPage from "../components/Portfolio/Trading212/Trading212CsvUploadPage";
import Trading212ApiPage from "../components/Portfolio/Trading212/Trading212ApiPage";
import ViewCompanyEarningsLoader from "../components/Dashboard/Charts/ViewCompanyEarningsLoader";
import EarningsPage from "../components/Dashboard/Earnings/EarningsPage";
import SuperInvestorsPage from "../components/SuperInvestors/SuperInvestorsPage";
import SuperInvestorHoldingsV2 from "../components/SuperInvestors/SuperInvestorHoldingsV2";
import CongressionalMemberDetail from "../components/SuperInvestors/CongressionalMemberDetail";
import TermsPage from "../pages/legal/TermsPage";
import PrivacyPage from "../pages/legal/PrivacyPage";
import SupportPage from "../pages/support/SupportPage";
import ProjectionsPage from "../components/Dashboard/Projections/ProjectionsPage";
import { StartFreeTrialPage } from "../components/Membership/StartFreeTrialPage";
import AppShell from "./AppShell";
import OnboardingFlow from "../components/Native/onboarding/OnboardingFlow";
import { isOnboardingSignupPending } from "../components/Native/onboarding/onboardingStorage";
import { hasSeenNativeIntro, isNative } from "../lib/platform";
import { lazyWithReload } from "../utils/lazyWithReload";
import { APP_SHELL_ROUTE_PATHS } from "./appShellRoutePaths";
import InboxRoute from "../components/Notifications/InboxRoute";
import ManageAlertsPage from "../components/Notifications/ManageAlertsPage";
import NotificationLinkPage from "../components/Notifications/NotificationLinkPage";
import UnsubscribePage from "../components/Notifications/UnsubscribePage";
import PublicationsPage from "../components/Publications/PublicationsPage";

const ChatPage = lazyWithReload(() => import("../components/Chat/ChatPage"));
const ScreenerPage = lazyWithReload(() => import("../components/Screener/ScreenerPage"));
// Lazy: the post view pulls in react-markdown, which otherwise only ships
// inside the (also lazy) chat chunk.
const PublicationPage = lazyWithReload(
  () => import("../components/Publications/PublicationPage"),
);
const AdminLayout = lazyWithReload(() => import("../pages/admin/AdminLayout"));
const AdminTodaysPlan = lazyWithReload(() => import("../pages/admin/TodaysPlan"));
const AdminRecentContent = lazyWithReload(() => import("../pages/admin/RecentContent"));
const AdminCuratedCompanies = lazyWithReload(() => import("../pages/admin/CuratedCompanies"));
const AdminDroppedArticles = lazyWithReload(() => import("../pages/admin/DroppedArticles"));
const AdminSettings = lazyWithReload(() => import("../pages/admin/Settings"));
const AdminAiUsage = lazyWithReload(() => import("../pages/admin/AiUsage"));
const AdminPublications = lazyWithReload(() => import("../pages/admin/Publications"));

const AdminSuspenseFallback = (
  <Stack alignItems="center" justifyContent="center" minHeight="100vh">
    <CircularProgress />
  </Stack>
);

// Native first launch lands on the onboarding quiz; a completed quiz that
// hasn't been through auth yet lands on sign-up (their plan is waiting);
// everyone else goes to sign-in (logged-in users bounce onward via
// GuestOnlyRoute).
const RootRedirect = () => {
  if (isNative() && !hasSeenNativeIntro()) {
    return <Navigate to="/onboarding" replace />;
  }
  if (isNative() && isOnboardingSignupPending()) {
    return <Navigate to="/sign-up?from=onboarding" replace />;
  }
  return <Navigate to="/sign-in" replace />;
};

export const Routes = (
  <>
    <Route path="/" element={<RootRedirect />} />

    {/* Public legal pages */}
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/privacy-policy" element={<PrivacyPage />} />
    <Route path="/support" element={<SupportPage />} />

    {/* Email verification and password reset - accessible to everyone (logged in or not) */}
    <Route path="/verify-email" element={<VerifyEmailPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />

    {/* Where an email's unsubscribe link lands. Public on purpose: it must
        work signed out, so it is also in the two 401-exception lists
        (singleton/axios.ts, hooks/useCurrencySettings.ts). */}
    <Route path="/unsubscribe" element={<UnsubscribePage />} />

    {/* Guest-only routes - redirect to dashboard if already logged in */}
    <Route element={<GuestOnlyRoute />}>
      <Route path="/sign-in" element={<LoginPage />} />
      <Route path="/sign-up" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/:pathname" element={<AuthPage />} />
      {/* Native first-launch onboarding quiz */}
      <Route path="/onboarding" element={<OnboardingFlow />} />
    </Route>

    <Route path="/auth/settings" element={<AccountSettings />} />

    <Route element={<AuthProtectedRoute />}>
      {/* ALL ROUTES BELOW HERE REQUIRE LOGIN */}

      {/* Admin-only news editorial controls. Lazy-loaded so non-admins
          never download the chunk. Bypasses ActiveMembershipProtectedRoute
          because admin access is independent of subscription state.
          Excluded from the native app entirely (web-only tooling). */}
      {!isNative() && (
      <Route element={<AdminProtectedRoute />}>
        <Route
          path="/admin"
          element={
            <Suspense fallback={AdminSuspenseFallback}>
              <AdminLayout />
            </Suspense>
          }
        >
          <Route index element={<Navigate to="todays-plan" replace />} />
          <Route
            path="todays-plan"
            element={<AdminTodaysPlan />}
          />
          <Route
            path="recent-content"
            element={<AdminRecentContent />}
          />
          {/* Back-compat redirect: bookmarks pointing at the old path land
              on the renamed view rather than 404'ing. */}
          <Route
            path="recent-articles"
            element={<Navigate to="/admin/recent-content" replace />}
          />
          <Route
            path="curated-companies"
            element={<AdminCuratedCompanies />}
          />
          <Route
            path="dropped-articles"
            element={<AdminDroppedArticles />}
          />
          <Route
            path="publications"
            element={<AdminPublications />}
          />
          {/* An open post (or "new"): the same page, so Back returns to the list. */}
          <Route
            path="publications/:postId"
            element={<AdminPublications />}
          />
          <Route
            path="ai-usage"
            element={<AdminAiUsage />}
          />
          <Route
            path="settings"
            element={<AdminSettings />}
          />
        </Route>
      </Route>
      )}

      {/* Legacy /membership route redirects to /start-free-trial */}
      <Route path="/membership" element={<Navigate to="/start-free-trial" replace />} />

      <Route path="/membership-success" element={<MembershipSuccess />} />
      {/* ALL ROUTES BELOW HERE REQUIRE AN ACTIVE MEMBERSHIP */}
      <Route element={<ActiveMembershipProtectedRoute />}>
        <Route
          element={
            <SidebarProvider>
              <AppShell />
            </SidebarProvider>
          }
        >
          {/* Paths come from APP_SHELL_ROUTE_PATHS so the notification
              allow-list can be tested against the real route table. */}
          <Route path={APP_SHELL_ROUTE_PATHS.startFreeTrial} element={<StartFreeTrialPage />} />
          <Route path={APP_SHELL_ROUTE_PATHS.dashboard} element={<Dashboard />} />
          <Route
            path={APP_SHELL_ROUTE_PATHS.chat}
            element={
              <Suspense fallback={AdminSuspenseFallback}>
                <ChatPage />
              </Suspense>
            }
          />
          <Route
            path={APP_SHELL_ROUTE_PATHS.screener}
            element={
              <Suspense fallback={AdminSuspenseFallback}>
                <ScreenerPage />
              </Suspense>
            }
          />
          <Route
            path={APP_SHELL_ROUTE_PATHS.savedScreen}
            element={
              <Suspense fallback={AdminSuspenseFallback}>
                <ScreenerPage />
              </Suspense>
            }
          />
          <Route path={APP_SHELL_ROUTE_PATHS.charts} element={<ChartsPage />} />
          <Route path={APP_SHELL_ROUTE_PATHS.compare} element={<ComparisonPage />} />
          <Route path={APP_SHELL_ROUTE_PATHS.projections} element={<ProjectionsPage />} />
          <Route path={APP_SHELL_ROUTE_PATHS.portfolio} element={<PortfolioPage />} />
          <Route path={APP_SHELL_ROUTE_PATHS.portfolioManage} element={<PortfolioManagePage />} />
          <Route path={APP_SHELL_ROUTE_PATHS.earnings} element={<EarningsPage key="home" />} />
          {/* Deep link into the calendar tab; keeps existing bookmarks working.
              Keyed so the two routes remount rather than share one element and
              carry a deep link's tab into the Home route. */}
          <Route
            path={APP_SHELL_ROUTE_PATHS.earningsCalendar}
            element={<EarningsPage key="calendar" initialTab="calendar" />}
          />
          <Route
            path={APP_SHELL_ROUTE_PATHS.earningsCompany}
            element={<ViewCompanyEarningsLoader />}
          />
          <Route path={APP_SHELL_ROUTE_PATHS.superInvestors} element={<SuperInvestorsPage />} />
          <Route
            path={APP_SHELL_ROUTE_PATHS.superInvestor}
            element={<SuperInvestorHoldingsV2 />}
          />
          <Route
            path={APP_SHELL_ROUTE_PATHS.congressionalMember}
            element={<CongressionalMemberDetail />}
          />
          <Route
            path={APP_SHELL_ROUTE_PATHS.trading212}
            element={<Trading212ConfigurationPage />}
          />
          <Route
            path={APP_SHELL_ROUTE_PATHS.trading212Csv}
            element={<Trading212CsvUploadPage />}
          />
          <Route
            path={APP_SHELL_ROUTE_PATHS.trading212Api}
            element={<Trading212ApiPage />}
          />
          <Route path={APP_SHELL_ROUTE_PATHS.inbox} element={<InboxRoute />} />
          <Route path={APP_SHELL_ROUTE_PATHS.manageAlerts} element={<ManageAlertsPage />} />
          {/* Where notification emails and Universal Links land. An ordinary
              protected route, so a signed-out click is carried through sign-in
              by AuthProtectedRoute's remembered destination. */}
          <Route path={APP_SHELL_ROUTE_PATHS.notificationLink} element={<NotificationLinkPage />} />
          <Route path={APP_SHELL_ROUTE_PATHS.publications} element={<PublicationsPage />} />
          <Route
            path={APP_SHELL_ROUTE_PATHS.publication}
            element={
              <Suspense fallback={AdminSuspenseFallback}>
                <PublicationPage />
              </Suspense>
            }
          />
          {/* Anything nothing else matches — a mistyped URL, or a destination
              the notification allow-list accepts but this build doesn't route
              (the screener) — opens the inbox INSIDE the shell. Without it the
              root errorElement renders outside the shell: a dead end on
              native, with no tab bar. */}
          <Route path="*" element={<Navigate to={APP_SHELL_ROUTE_PATHS.inbox} replace />} />
        </Route>
      </Route>
    </Route>
  </>
);
